import {
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, MessageSquarePlus, Send, ShieldCheck } from 'lucide-react';
import {
  AliceHeader,
  SafeMarkdown,
  aliceAvatarUrl,
  aliceConversationIdentity,
  useAliceConversation,
} from '@command-center/oneup-portal-ui';
import { Badge, Button, cn } from '@command-center/ui-system';
import { useAuth } from '../auth/AuthProvider';
import { ApiClientError } from '../lib/api';
import { askAlice, type AliceContext, type AliceLaunch, type AliceMessage } from '../lib/alice-api';
import { adminConfig } from '../platform/config';

interface AliceDrawerProps {
  seed: AliceLaunch | null;
  onCollapse: () => void;
}

/**
 * Derive Alice's default page scope. Feed cards can override it via AliceLaunch.
 *
 * The open record wins over the page it sits on: Form Entries and Calls keep the
 * selection in a query parameter rather than the path, so without reading the
 * search string an open entry looked identical to the list it came from.
 */
function useRouteContext(): { context?: AliceContext; label: string } {
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);

  const leadMatch = /^\/leads\/([^/]+)$/.exec(pathname);
  if (leadMatch) return { context: { kind: 'lead', id: leadMatch[1]! }, label: 'this lead' };

  const contactMatch = /^\/contacts\/([^/]+)$/.exec(pathname);
  if (contactMatch) {
    return { context: { kind: 'contact', id: contactMatch[1]! }, label: 'this contact' };
  }

  const organizationMatch = /^\/organizations\/([^/]+)$/.exec(pathname);
  if (organizationMatch) {
    return {
      context: { kind: 'organization', id: organizationMatch[1]! },
      label: 'this organization',
    };
  }

  const entryId = params.get('entry');
  if (pathname === '/forms-feed' && entryId) {
    return { context: { kind: 'form_entry', id: entryId }, label: 'this form entry' };
  }
  if (pathname === '/forms-feed') return { label: 'Form Entries' };

  const callId = params.get('call');
  if (pathname.startsWith('/ai-systems') && callId) {
    return { context: { kind: 'call', id: callId }, label: 'this call' };
  }
  if (pathname.startsWith('/ai-systems')) return { label: 'Calls' };

  if (pathname === '/leadloop') return { label: 'the LeadLoop queue' };
  if (pathname === '/leads') return { label: 'Leads' };
  if (pathname === '/contacts') return { label: 'Contacts' };
  if (pathname === '/organizations') return { label: 'Organizations' };
  if (pathname === '/emails') return { label: 'Emails' };
  return { label: 'Cascade Command Center' };
}

const WELCOME_MESSAGE: AliceMessage = {
  role: 'assistant',
  content:
    "Hi, I'm Alice. I can look through your leads, calls, form entries, and the LeadLoop queue — "
    + 'and I can write notes, set a lead\'s status or follow-up date, and turn an entry or call into '
    + 'a lead once you confirm it. Ask me what needs attention, or open a record and ask about it.',
};

export function AliceDrawer({ seed, onCollapse }: AliceDrawerProps) {
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const handledSeedRef = useRef<number | null>(null);
  const routeScope = useRouteContext();
  const context = seed?.context ?? routeScope.context;
  const label = seed?.context ? (seed.contextLabel ?? 'the selected lead') : routeScope.label;
  const { user, impersonation } = useAuth();
  /**
   * One conversation per signed-in identity, not per record.
   *
   * Cascade carried the same reset-on-context-change effect MPF did: opening a
   * different lead wiped everything that had been said. Which lead is on screen
   * is a per-turn input (`context` below), not a reason to forget the thread.
   */
  const conversation = useAliceConversation<AliceMessage>({
    storageKey: 'cascade:alice-conversation',
    identity: aliceConversationIdentity({
      userId: user?.id ?? null,
      appId: 'admin-cascade',
      supportSessionId: impersonation?.impersonation.sessionId ?? null,
    }),
    welcome: WELCOME_MESSAGE,
  });
  const { messages, read: readMessages, setMessages: replaceMessages, append: appendMessage } = conversation;
  const aliceConfig = adminConfig.alice;
  const assistantName = aliceConfig?.assistantName ?? 'Alice';
  const avatarPath = aliceConfig?.avatarPath ?? aliceAvatarUrl;
  useEffect(() => {
    const timer = window.setTimeout(() => textareaRef.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages, pending]);

  const send = useCallback(
    async (prompt: string) => {
      const trimmed = prompt.trim();
      if (!trimmed || pending) return;
      const next: AliceMessage[] = [...readMessages(), { role: 'user', content: trimmed }];
      replaceMessages(next);
      setInput('');
      setPending(true);
      try {
        const { message } = await askAlice(next, context);
        appendMessage(message);
      } catch (err) {
        appendMessage({ role: 'assistant', content: formatError(err) });
      } finally {
        setPending(false);
      }
    },
    [appendMessage, context, pending, readMessages, replaceMessages],
  );

  useEffect(() => {
    if (!seed || handledSeedRef.current === seed.nonce) return;
    handledSeedRef.current = seed.nonce;
    if (seed.prompt.trim()) void send(seed.prompt);
  }, [seed, send]);

  const handleSubmit = useCallback(
    (event?: FormEvent) => {
      event?.preventDefault();
      void send(input);
    },
    [input, send],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        void send(input);
      }
    },
    [input, send],
  );

  const panel = (
    <div className="flex h-full min-h-0 flex-col bg-card text-card-foreground">
      <AliceHeader
        assistantName={assistantName}
        avatarPath={avatarPath}
        subtitle="Context-aware help for this workspace."
        badge={
          <Badge className="border-emerald-500/30 bg-emerald-500/10 text-[10px] text-emerald-700 hover:bg-emerald-500/10 dark:text-emerald-400">
            <span className="mr-1 h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {pending ? 'Thinking' : 'Online'}
          </Badge>
        }
        metaLabel={`${aliceConfig?.defaultModelLabel ?? 'Default'} · scoped to ${label}`}
        onCollapse={onCollapse}
      />

      <div className="border-b border-border bg-muted/20 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5 text-primary" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-[12px] text-muted-foreground">
            Using <span className="font-medium text-foreground">{label}</span> as context
          </span>
        </div>
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 && !pending ? (
          <div className="rounded-lg border border-border bg-background/70 px-3 py-3 text-[12px] text-muted-foreground">
            <p className="font-medium text-foreground">Ask Alice about this queue or record.</p>
            <p className="mt-1">
              She reads leads, calls, form entries, and the LeadLoop queue, and can write notes and
              set follow-ups. Creating a lead, linking one, or dismissing a queue item always waits
              for you to confirm.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((message, index) => (
              <MessageRow key={`${message.role}-${index}`} message={message} />
            ))}
            {pending && (
              <div className="flex items-center gap-2 px-2 py-1 text-[12px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                Alice is thinking…
              </div>
            )}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="border-t border-border bg-background/70 p-3">
        <div className="rounded-lg border border-border bg-background focus-within:ring-1 focus-within:ring-ring">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Ask ${assistantName} to draft, summarize, or plan…`}
            rows={4}
            disabled={pending}
            aria-label="Message Alice"
            className="max-h-44 min-h-[96px] w-full resize-y rounded-t-lg bg-transparent px-3 py-2.5 text-[13px] leading-relaxed outline-none placeholder:text-muted-foreground disabled:opacity-60"
          />
          <div className="flex items-center justify-between gap-2 border-t border-border/70 px-2.5 py-2">
            {/*
              The conversation now persists across navigation, so ending it has
              to be something the user chooses rather than a side effect of
              opening a different lead.
            */}
            <button
              type="button"
              onClick={conversation.clear}
              disabled={!conversation.hasHistory || pending}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
            >
              <MessageSquarePlus className="h-3 w-3" aria-hidden />
              New conversation
            </button>
            <div className="flex items-center gap-2">
              <span className="hidden text-[10px] text-muted-foreground sm:inline">
                Cmd/Ctrl + Enter
              </span>
              <Button
                type="submit"
                size="sm"
                disabled={pending || !input.trim()}
                className="h-8 gap-1.5 px-3"
                aria-label="Send to Alice"
              >
                {pending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Send className="h-3.5 w-3.5" aria-hidden />
                )}
                Send
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );

  return panel;
}

function MessageRow({ message }: { message: AliceMessage }) {
  const isUser = message.role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[90%] rounded-xl p-3 text-sm leading-relaxed',
          isUser
            ? 'whitespace-pre-wrap bg-primary text-primary-foreground'
            : 'bg-muted text-foreground',
        )}
      >
        {isUser ? (
          message.content
        ) : (
          <SafeMarkdown markdown={message.content} emptyState="Alice returned an empty response." />
        )}
      </div>
    </div>
  );
}

function formatError(err: unknown): string {
  if (err instanceof ApiClientError) {
    if (err.code === 'openai_not_configured') {
      return "Alice isn't fully configured yet — add `OPENAI_API_KEY` to the Cascade Worker to enable live replies.";
    }
    if (err.code === 'alice_disabled') return 'Alice is turned off for this portal.';
    return err.message;
  }
  return err instanceof Error ? err.message : 'Alice request failed.';
}
